import { RequestProgressEvent } from "./http-client";

/**
 * Creates a service configuration factory with shared defaults
 * This factory simplifies service instantiation by providing sensible
 * defaults and reducing boilerplate configuration code.
 *
 * @example
 * // Create a factory instance
 * const factory = new ServiceFactory(caches);
 *
 * // Create a service with default configuration
 * const userService = factory.create(UserService);
 *
 * // Create a service with custom base URL
 * const insightsService = factory.create(InsightsService, {
 *   baseUrl: process.env.INSIGHTS_API_URL
 * });
 *
 * // Create a service with public paths (no auth required)
 * const accountService = factory.create(AccountService, {
 *   publicPaths: [
 *     new RegExp("^/auth/login/?$"),
 *     new RegExp("^/auth/reset/?$")
 *   ]
 * });
 *
 * // Create a service with custom timeout
 * const customService = factory.create(CustomService, {
 *   serviceConfig: { timeout: 600000 }
 * });
 */
export class ServiceFactory {
  /**
   * @param {object} caches - Cache dependencies (tokenCache, currentUserCache, etc.)
   */
  constructor(caches) {
    this.caches = caches;

    // Shared configuration for all services
    this.defaultServiceConfig = {
      timeout: 300000,
    };

    // Shared progress configuration
    this.defaultProgressConfig = {
      progressTimeout: 60000,
      progressTimeoutHandler: (url, status) => {
        const requestTimeoutEvent = new CustomEvent(RequestProgressEvent, {
          detail: { url, status },
        });
        document.dispatchEvent(requestTimeoutEvent);
      },
    };
  }

  /**
   * Creates a service instance with minimal configuration
   * @param {class} ServiceClass - The service class to instantiate
   * @param {object} options - Optional configuration overrides
   * @param {string} options.baseUrl - API base URL (defaults to 
   * DASHBOARD_API_URL)
   * @param {RegExp[]} options.publicPaths - Paths that don't require auth
   * @param {object} options.serviceConfig - Override service config
   * @param {object} options.progressConfig - Override progress config
   * @returns {object} Instance of the service class
   */
  create(ServiceClass, options = {}) {
    const {
      baseUrl = process.env.DASHBOARD_API_URL,
      publicPaths = [],
      serviceConfig = {},
      progressConfig = {},
    } = options;

    const finalServiceConfig = {
      publicPaths,
      ...this.defaultServiceConfig,
      ...serviceConfig,
    };

    const finalProgressConfig = {
      ...this.defaultProgressConfig,
      ...progressConfig,
    };

    return new ServiceClass(
      baseUrl,
      finalServiceConfig,
      this.caches,
      finalProgressConfig
    );
  }
}

